import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { GET_PROJECT_ALL_BRANDS_RESPONSE } from '../../../query/project/stats/keys';
import { ChatType } from '../../../../types/monitor/ChatType';
import { AllBrandsStatsResult } from '../../../../models/projects/stats/AllBrandsStatsResponse';
import { getProjectAllBrandsStats } from '../../../services/project/stats/ProjectDailyStatsService';
import { MonitorPeriod } from '../../../../types/monitor/MonitorPeriod';
import { MonitorPrompt } from '../../../../models/monitor/MonitorPrompt';
import { useMemo } from 'react';

export const useGetProjectAllBrandsStats = (
  projectId: number | null,
  filteredMonitorPrompts: MonitorPrompt[] | null,
  chatType: ChatType | null,
  period: MonitorPeriod | null,
  from?: Date | null,
  to?: Date | null,
  filterChatTypes?: string[]
) => {
  const queryClient = useQueryClient();
  const hasPrompts = !!filteredMonitorPrompts && filteredMonitorPrompts.length > 0;
  const queryEnabled = !!projectId && !!chatType && !!from && !!to && !!period && hasPrompts;

  const monitorPromptIds = useMemo(
    () => filteredMonitorPrompts?.map((p) => p.id).filter((id): id is number => id != null),
    [filteredMonitorPrompts]
  );

  const {
    data: allBrandsStatsResult,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery<AllBrandsStatsResult>({
    queryKey: [GET_PROJECT_ALL_BRANDS_RESPONSE, projectId, chatType, period, from, to, monitorPromptIds, filterChatTypes],
    queryFn: () => {
      return getProjectAllBrandsStats(projectId!, chatType!, period!, from, to, filterChatTypes, monitorPromptIds);
    },
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    enabled: queryEnabled,
    placeholderData: keepPreviousData,
  });

  return {
    // Unwrap `.brands` so existing consumers keep receiving the array unchanged (V1 + shared hooks).
    allBrandsStatsResponse: queryEnabled ? allBrandsStatsResult?.brands ?? [] : [],
    // New: project-level Visibility % numbers (null until loaded). Consumed by the V2 tile.
    visibility: queryEnabled ? allBrandsStatsResult?.visibility ?? null : null,
    isLoading: queryEnabled ? isLoading : false,
    isError: queryEnabled ? isError : false,
    error: queryEnabled ? error : null,
    refetch,
    invalidate: () => {
      return queryClient.invalidateQueries({
        queryKey: [GET_PROJECT_ALL_BRANDS_RESPONSE, projectId, chatType, period, from, to, monitorPromptIds, filterChatTypes],
        refetchType: 'active',
      });
    },
  };
};
